import React from 'react';
import { Modal } from 'components';
import { Button, Box } from '@mui/material';
import { OpenInNew as OpenInNewIcon } from '@mui/icons-material';
import { Icon, TypoGraph } from 'components';
import strings from 'strings';
import { SafetyDetails } from 'interfaces/IClaimsSearching';
import { formatEventTypeLabel, getEventIcon } from 'tools/helper';
import { useSelector } from 'react-redux';

interface ClaimsSafetyVideoModalProps {
    open: boolean;
    onClose: () => void;
    selectedEvent: SafetyDetails | undefined;
    selectedCarrierName: string;
    selectedEld: string;
    videoUrl: string;
}

const ClaimsSafetyVideoModal: React.FC<ClaimsSafetyVideoModalProps> = ({
    open,
    onClose,
    selectedEvent,
    selectedCarrierName,
    selectedEld,
    videoUrl
}) => {
    const t = strings.CLAIMS_DETAILS;
    const v = strings.CLAIMS_VIDEO_MODAL;

    const companyProfile = useSelector(
        (state: any) => state?.whiteLabel?.companyProfile
    )

    const handleOpenInNewTab = () => {
        if (videoUrl) {
            window.open(videoUrl, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <Modal
            open={open}
            handleClose={onClose}
            size="xl"
            fullWidth={true}
            title={`${selectedEld} Video - ${formatEventTypeLabel(selectedEvent?.type)} ${t.EVENT}`}
            dom={
                <Box sx={{ height: '90vh', display: 'flex', flexDirection: 'column' }}>
                    {/* Modal Header */}
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '8px 12px',
                        borderBottom: '1px solid #e0e0e0',
                        backgroundColor: '#f5f5f5',
                        maxHeight: '65px'
                    }}>
                        {/* Logo - Left */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {companyProfile?.logo ? (
                                <Icon
                                    type="images"
                                    url={companyProfile?.logo}
                                    boxClassName="fullLogo"
                                    width={100}
                                    height={50}
                                />
                            ) : (
                                <Icon
                                    name={'icon_logoFullname'}
                                    width={150}
                                    height={40}
                                />
                            )}
                        </Box>

                        {/* Event Name */}
                        <Box sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            flex: 1,
                            justifyContent: 'start',
                            marginLeft: '20px'
                        }}>
                            <Icon
                                size={24}
                                materialIcon={getEventIcon(selectedEvent?.type ?? '')}
                                width={24}
                                height={24}
                            />
                            <TypoGraph variant="h6" component="div">
                                {selectedEld} {t.VIDEO} - {formatEventTypeLabel(selectedEvent?.type)} {t.EVENT}
                            </TypoGraph>
                        </Box>

                        {/* Buttons - Right */}
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                            <Button
                                variant="contained"
                                startIcon={<OpenInNewIcon />}
                                onClick={handleOpenInNewTab}
                                size="small"
                                sx={{
                                    borderRadius: 25,
                                    textTransform: 'none',
                                    fontWeight: 'bold'
                                }}
                            >
                                {v.OPEN_IN_NEW_TAB}
                            </Button>
                            <Button
                                variant="outlined"
                                onClick={onClose}
                                size="small"
                                sx={{
                                    borderRadius: 25,
                                    textTransform: 'none',
                                    fontWeight: 'bold'
                                }}
                            >
                                {v.CLOSE}
                            </Button>
                        </Box>
                    </Box>

                    {/* Modal Content - Fullscreen iframe */}
                    <Box sx={{ flex: 1, position: 'relative' }}>
                        <iframe
                            src={videoUrl}
                            width="100%"
                            height="100%"
                            style={{
                                border: 'none',
                                minHeight: 'calc(90vh - 130px)'
                            }}
                            title={`${selectedEld} - ${selectedEvent?.type} - ${selectedCarrierName}`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </Box>

                    {/* Modal Footer */}
                    <Box sx={{
                        padding: '12px 24px',
                        borderTop: '1px solid #e0e0e0',
                        backgroundColor: '#f5f5f5',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <TypoGraph variant="body2" color="text.secondary">
                            {v.FLEET}: {selectedCarrierName}{" "}
                            &nbsp;&nbsp;|&nbsp;&nbsp; 
                            {v.ASSET}: {selectedEvent?.vehicle?.vehicleId?.assetEldId}{" "}
                            &nbsp;&nbsp;|&nbsp;&nbsp; 
                            {v.VIN}: {selectedEvent?.vehicle?.vin}
                            &nbsp;&nbsp;|&nbsp;&nbsp; 
                            {v.TIME}: {selectedEvent?.time ? new Date(selectedEvent.time).toLocaleString() : v.NO_DATA}{" "}
                        </TypoGraph>
                    </Box>
                </Box>
            }
        />
    );
};

export default ClaimsSafetyVideoModal;
